import * as React from 'react';
import moment from 'moment'
import {LocalizationProvider} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {useEffect, useMemo, useState} from "react";
import {FormControl, InputLabel, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import PropTypes from 'prop-types'
import useRootHook from '../../hooks/useRoot';
import paymethods from '../../models/paymethods';

const DATE_FORMAT = 'DD/MM/YYYY'

function StatsContainer(props) {
    const {fullPage, setFilters, filters} = props
    const [filterType, setFilterType] = useState('day')
    const {userData} = useRootHook();
    const [paymethodsData, setPaymethodsData] = useState(null);

    const canUsePaymethods = useMemo(() => {
        return !!userData?.urls?.includes('/paymethods');
    }, [userData]);

    useEffect(() => {
        if (!fullPage || !canUsePaymethods) return;
        paymethods.index()
            .then((data) => setPaymethodsData(data || []))
            .catch(() => setPaymethodsData([]));
    }, [fullPage, canUsePaymethods]);

    const handleSetInterval = (ev, type) => {
        const date = moment.utc()
        switch (type) {
            case 'day':
                setFilterType(type)
                setFilters(prevFilters => {
                    return {
                        ...prevFilters,
                        from: date.startOf('day').toISOString(),
                        to: date.endOf('day').toISOString()
                    }
                })
                break;
            case 'week':
                setFilterType(type)
                setFilters(prevFilters => {
                    const from = date.clone().subtract(1, 'week')
                    return {
                        ...prevFilters,
                        from: from.startOf('day').toISOString(),
                        to: date.endOf('day').toISOString()
                    }
                })
                break;
            case 'month':
                setFilterType(type)
                setFilters(prevFilters => {
                    const from = date.clone().subtract(1, 'month')
                    return {
                        ...prevFilters,
                        from: from.startOf('day').toISOString(),
                        to: date.endOf('day').toISOString()
                    }
                })
                break;
            default:
                break;
        }
    }

    return (
        <React.Fragment>
            <Stack spacing={2} sx={{mb: 2}} direction="row" justifyContent="flex-end">
                {fullPage && canUsePaymethods && (
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Payment method</InputLabel>
                        <Select
                            label="Payment method"
                            value={filters.paymethod_id ?? ''}
                            onChange={(e) => {
                                const next = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                setFilters((prev) => ({...prev, paymethod_id: next}));
                            }}
                        >
                            <MenuItem value="">All methods</MenuItem>
                            {(paymethodsData || []).map((pm) => (
                                <MenuItem key={`pm-${pm.id}`} value={pm.id}>
                                    {pm.paysystem?.key ? `${pm.paysystem.key.toUpperCase()}: ` : ''}{pm.name}
                                    {pm.paysystem?.key === 'stripe' && pm.stripe_tag ? ` · ${pm.stripe_tag}` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <ToggleButtonGroup
                    value={filterType}
                    size="small"
                    exclusive
                    onChange={handleSetInterval}
                    aria-label="Date interval"
                >
                    <ToggleButton value="day" aria-label="left aligned">
                        Day
                    </ToggleButton>
                    <ToggleButton value="week" aria-label="centered">
                        Week
                    </ToggleButton>
                    <ToggleButton value="month" aria-label="right aligned">
                        Month
                    </ToggleButton>
                </ToggleButtonGroup>
                {fullPage && <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        label="From"
                        disableFuture={true}
                        value={moment.utc(filters.from)}
                        inputFormat={DATE_FORMAT}
                        onChange={(newValue) => {
                            setFilters(prevFilters => {
                                setFilterType(null)
                                let newFilters = {...prevFilters, from: newValue.startOf('day').toISOString()}
                                if (moment.utc(prevFilters.to).isBefore(newValue)) {
                                    newFilters.to = newValue.endOf('day').toISOString()
                                }
                                return newFilters
                            });
                        }}
                        renderInput={(params) => <TextField size="small" {...params} />}
                    />
                </LocalizationProvider>
                }
                {fullPage && <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        label="To"
                        disableFuture={true}
                        value={moment.utc(filters.to)}
                        inputFormat={DATE_FORMAT}
                        onChange={(newValue) => {
                            setFilters(prevFilters => {
                                setFilterType(null)
                                let newFilters = {...prevFilters, to: newValue.endOf('day').toISOString()}
                                if (moment.utc(prevFilters.from).isAfter(newValue)) {
                                    newFilters.from = newValue.startOf('day').toISOString()
                                }
                                return newFilters
                            });
                        }}
                        renderInput={(params) => <TextField size="small" {...params} />}
                    />
                </LocalizationProvider>
                }
            </Stack>
            {props.children}
        </React.Fragment>
    );
}

StatsContainer.propTypes = {
    fullPage: PropTypes.bool,
    filters: PropTypes.object,
    setFilters: PropTypes.func
}

export default StatsContainer